/*
 * Feminine, minimal SVG demonstrations for the three core facial exercises.
 * All strokes use the app palette (--clay, --terracotta, --moss, --sand)
 * via CSS variables inline, and each visual carries a gentle looping SMIL
 * animation so the user can see the movement without playing/pausing anything.
 *
 * Sizes are set by the parent — pass `size` in px (default 72).
 */
import React from "react";

const CLAY = "hsl(var(--clay))";
const TERRA = "hsl(var(--terracotta))";
const MOSS = "hsl(var(--moss))";
const MUTED = "hsl(var(--muted-foreground))";

/**
 * Shared feminine face — soft oval, closed serene eyes with lashes, brow hint.
 * Kept intentionally minimal so the animated overlay reads clearly.
 */
function FaceBase({ children }) {
  return (
    <>
      {/* face oval */}
      <ellipse cx="60" cy="62" rx="36" ry="46" fill="hsl(var(--paper))" stroke={CLAY} strokeWidth="1.5" />
      {/* hair suggestion — a soft crown line */}
      <path d="M 24 40 Q 60 12 96 40" fill="none" stroke={CLAY} strokeWidth="1.5" strokeLinecap="round" />
      {/* left eye (closed, feminine lash) */}
      <path d="M 40 60 Q 46 63 52 60" fill="none" stroke={CLAY} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M 40 60 L 39 58 M 43 61 L 43 59 M 46 61 L 46 59 M 49 61 L 49 59 M 52 60 L 53 58"
            stroke={CLAY} strokeWidth="1" strokeLinecap="round" />
      {/* right eye */}
      <path d="M 68 60 Q 74 63 80 60" fill="none" stroke={CLAY} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M 68 60 L 67 58 M 71 61 L 71 59 M 74 61 L 74 59 M 77 61 L 77 59 M 80 60 L 81 58"
            stroke={CLAY} strokeWidth="1" strokeLinecap="round" />
      {/* soft lips */}
      <path d="M 54 84 Q 60 87 66 84" fill="none" stroke={TERRA} strokeWidth="1.4" strokeLinecap="round" />
      {/* cheek blush hint — very subtle */}
      <circle cx="42" cy="74" r="4" fill={TERRA} opacity="0.14" />
      <circle cx="78" cy="74" r="4" fill={TERRA} opacity="0.14" />
      {children}
    </>
  );
}

/* ---------- Cheek lift (muscle-activated) ----------
 * Not a hand-glide any more — this is a smile-lift powered by the
 * zygomatic muscles. Two soft arrows rise from the corners of the mouth
 * up into the cheekbones and back, mirroring the hold-release cycle of
 * the exercise. Lips visibly lift and cheeks blush more intensely on the
 * "hold" phase.
 */
export function CheekLiftSVG({ size = 72, label = "Cheek lift demo — muscle activation" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" role="img" aria-label={label} data-testid="svg-cheek-lift">
      <FaceBase>
        {/* Lifted-smile lips overlay — fades in during the "hold" phase */}
        <path d="M 52 82 Q 60 78 68 82" fill="none" stroke={TERRA} strokeWidth="1.6" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.35;0.5;0.85;1" dur="3s" repeatCount="indefinite" />
        </path>
        {/* Extra blush on the hold — cheeks visibly lift */}
        <circle cx="42" cy="72" r="4.5" fill={TERRA} opacity="0">
          <animate attributeName="opacity" values="0;0;0.38;0.38;0" keyTimes="0;0.35;0.5;0.85;1" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="78" cy="72" r="4.5" fill={TERRA} opacity="0">
          <animate attributeName="opacity" values="0;0;0.38;0.38;0" keyTimes="0;0.35;0.5;0.85;1" dur="3s" repeatCount="indefinite" />
        </circle>
        {/* Left activation arrow — corner of mouth up into cheekbone */}
        <path d="M 52 84 Q 46 78 44 68" fill="none" stroke={TERRA} strokeWidth="2.2" strokeLinecap="round" strokeDasharray="34" strokeDashoffset="34">
          <animate attributeName="stroke-dashoffset" values="34;0;0;34" keyTimes="0;0.45;0.85;1" dur="3s" repeatCount="indefinite" />
        </path>
        <path d="M 44 68 L 41 71 M 44 68 L 47 71" stroke={TERRA} strokeWidth="2.2" strokeLinecap="round" fill="none" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.4;0.5;0.85;1" dur="3s" repeatCount="indefinite" />
        </path>
        {/* Right activation arrow */}
        <path d="M 68 84 Q 74 78 76 68" fill="none" stroke={TERRA} strokeWidth="2.2" strokeLinecap="round" strokeDasharray="34" strokeDashoffset="34">
          <animate attributeName="stroke-dashoffset" values="34;0;0;34" keyTimes="0;0.45;0.85;1" dur="3s" repeatCount="indefinite" />
        </path>
        <path d="M 76 68 L 73 71 M 76 68 L 79 71" stroke={TERRA} strokeWidth="2.2" strokeLinecap="round" fill="none" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.4;0.5;0.85;1" dur="3s" repeatCount="indefinite" />
        </path>
      </FaceBase>
    </svg>
  );
}

/* ---------- Face stretch + lymphatic drainage ----------
 * A big yawn-style open-mouth stretch that pulses through the whole face,
 * followed by two downward sweeps along the sides of the neck showing
 * lymph moving toward the collarbones.
 */
export function FaceStretchDrainSVG({ size = 72, label = "Face stretch and neck drainage demo" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 145" role="img" aria-label={label} data-testid="svg-face-stretch-drain">
      <FaceBase>
        {/* Neck silhouette */}
        <path d="M 44 106 Q 42 122 40 138" fill="none" stroke={CLAY} strokeWidth="1.4" strokeLinecap="round" opacity="0.65" />
        <path d="M 76 106 Q 78 122 80 138" fill="none" stroke={CLAY} strokeWidth="1.4" strokeLinecap="round" opacity="0.65" />
        <path d="M 40 138 Q 60 141 80 138" fill="none" stroke={CLAY} strokeWidth="1.2" strokeLinecap="round" opacity="0.35" />

        {/* Big stretched-open mouth — animates from soft lips to wide open and back */}
        <ellipse cx="60" cy="86" rx="9" ry="7" fill={TERRA} opacity="0">
          <animate attributeName="rx" values="1;9;9;1"  keyTimes="0;0.35;0.55;1" dur="3.6s" repeatCount="indefinite" />
          <animate attributeName="ry" values="1;7;7;1"  keyTimes="0;0.35;0.55;1" dur="3.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.55;0.55;0" keyTimes="0;0.35;0.55;1" dur="3.6s" repeatCount="indefinite" />
        </ellipse>

        {/* Left downward drainage sweep — appears after the stretch releases */}
        <path
          id="stretch-drain-l"
          d="M 34 96 Q 32 112 34 134"
          fill="none"
          stroke={MOSS}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeDasharray="60"
          strokeDashoffset="60"
        >
          <animate attributeName="stroke-dashoffset" values="60;60;0;0;60" keyTimes="0;0.55;0.9;0.95;1" dur="3.6s" repeatCount="indefinite" />
        </path>
        {/* Right downward drainage sweep */}
        <path
          id="stretch-drain-r"
          d="M 86 96 Q 88 112 86 134"
          fill="none"
          stroke={MOSS}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeDasharray="60"
          strokeDashoffset="60"
        >
          <animate attributeName="stroke-dashoffset" values="60;60;0;0;60" keyTimes="0;0.55;0.9;0.95;1" dur="3.6s" repeatCount="indefinite" />
        </path>

        {/* Downward arrows at the base of each sweep */}
        <path d="M 34 134 L 31 130 M 34 134 L 37 130" fill="none" stroke={MOSS} strokeWidth="2.2" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.7;0.85;0.95;1" dur="3.6s" repeatCount="indefinite" />
        </path>
        <path d="M 86 134 L 83 130 M 86 134 L 89 130" fill="none" stroke={MOSS} strokeWidth="2.2" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.7;0.85;0.95;1" dur="3.6s" repeatCount="indefinite" />
        </path>
      </FaceBase>
    </svg>
  );
}

/* ---------- Full-face yoga — alternating O and E shapes ----------
 * The finisher. Alternates between a round "O" (small tall circle mouth,
 * lifted cheeks) and a wide "E" (horizontal line mouth, blush wider) so
 * every muscle group works in one flowing rhythm.
 */
export function FullFaceYogaSVG({ size = 72, label = "Full-face O to E demo" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" role="img" aria-label={label} data-testid="svg-full-face-yoga">
      <FaceBase>
        {/* "O" mouth — a tall vertical ellipse, visible during the first half */}
        <ellipse cx="60" cy="84" rx="3.5" ry="5.5" fill={TERRA} opacity="0">
          <animate attributeName="opacity" values="0.9;0.9;0;0;0.9" keyTimes="0;0.4;0.5;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </ellipse>
        {/* Cheek lift markers for "O" phase */}
        <circle cx="42" cy="72" r="5" fill={TERRA} opacity="0">
          <animate attributeName="opacity" values="0.35;0.35;0;0;0.35" keyTimes="0;0.4;0.5;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="78" cy="72" r="5" fill={TERRA} opacity="0">
          <animate attributeName="opacity" values="0.35;0.35;0;0;0.35" keyTimes="0;0.4;0.5;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </circle>

        {/* "E" mouth — a wide horizontal line, visible during the second half */}
        <path d="M 46 84 L 74 84" stroke={TERRA} strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0">
          <animate attributeName="opacity" values="0;0;0.95;0.95;0" keyTimes="0;0.5;0.55;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </path>
        {/* Slight jaw stretch marks for "E" phase */}
        <path d="M 42 90 L 40 96" stroke={TERRA} strokeWidth="1.6" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;0.7;0.7;0" keyTimes="0;0.5;0.6;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </path>
        <path d="M 78 90 L 80 96" stroke={TERRA} strokeWidth="1.6" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;0.7;0.7;0" keyTimes="0;0.5;0.6;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </path>

        {/* Small O / E labels near the corner — help the visual read clearly */}
        <text x="98" y="30" fontSize="8" fill={CLAY} fontFamily="serif" opacity="0">
          O
          <animate attributeName="opacity" values="0.8;0.8;0;0;0.8" keyTimes="0;0.4;0.5;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </text>
        <text x="98" y="30" fontSize="8" fill={CLAY} fontFamily="serif" opacity="0">
          E
          <animate attributeName="opacity" values="0;0;0.8;0.8;0" keyTimes="0;0.5;0.55;0.95;1" dur="3.2s" repeatCount="indefinite" />
        </text>
      </FaceBase>
    </svg>
  );
}

/* ---------- Jaw-line glide → neck drainage ----------
 * Two-part motion: up the jawline from chin to just below the ear, then
 * DOWN the side of the neck toward the collarbone. Encourages lymphatic
 * drainage. Traced with a compound path and a fingertip dot that rides
 * the full stroke on each side.
 */
export function JawGlideSVG({ size = 72, label = "Jaw and neck lymphatic drainage demo" }) {
  // Expanded viewBox so the neck can extend below the face oval.
  return (
    <svg width={size} height={size} viewBox="0 0 120 145" role="img" aria-label={label} data-testid="svg-jaw-glide">
      <FaceBase>
        {/* Neck silhouette — soft, minimal lines so the drainage path reads clearly. */}
        <path d="M 44 106 Q 42 122 40 138" fill="none" stroke={CLAY} strokeWidth="1.4" strokeLinecap="round" opacity="0.65" />
        <path d="M 76 106 Q 78 122 80 138" fill="none" stroke={CLAY} strokeWidth="1.4" strokeLinecap="round" opacity="0.65" />
        <path d="M 40 138 Q 60 141 80 138" fill="none" stroke={CLAY} strokeWidth="1.2" strokeLinecap="round" opacity="0.35" />

        {/* LEFT drainage path: chin → up jaw → down side of neck. */}
        <path
          id="jaw-drain-l"
          d="M 60 108 Q 45 108 33 92 Q 26 82 26 72 L 30 90 Q 34 112 38 134"
          fill="none"
          stroke={MOSS}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeDasharray="140"
          strokeDashoffset="140"
        >
          <animate attributeName="stroke-dashoffset" values="140;0;0;140" keyTimes="0;0.55;0.85;1" dur="3.4s" repeatCount="indefinite" />
        </path>

        {/* RIGHT drainage path: mirrored. */}
        <path
          id="jaw-drain-r"
          d="M 60 108 Q 75 108 87 92 Q 94 82 94 72 L 90 90 Q 86 112 82 134"
          fill="none"
          stroke={MOSS}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeDasharray="140"
          strokeDashoffset="140"
        >
          <animate attributeName="stroke-dashoffset" values="140;0;0;140" keyTimes="0;0.55;0.85;1" dur="3.4s" repeatCount="indefinite" />
        </path>

        {/* Small downward arrow at the neck end to reinforce the drainage direction. */}
        <path d="M 38 134 L 35 130 M 38 134 L 41 130" fill="none" stroke={MOSS} strokeWidth="2.2" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.55;0.65;0.85;1" dur="3.4s" repeatCount="indefinite" />
        </path>
        <path d="M 82 134 L 79 130 M 82 134 L 85 130" fill="none" stroke={MOSS} strokeWidth="2.2" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.55;0.65;0.85;1" dur="3.4s" repeatCount="indefinite" />
        </path>

        {/* Fingertip dot riding the left full drainage path. */}
        <circle r="3.5" fill={TERRA}>
          <animateMotion dur="3.4s" repeatCount="indefinite" keyPoints="0;1;1;0" keyTimes="0;0.55;0.85;1" calcMode="linear">
            <mpath href="#jaw-drain-l" />
          </animateMotion>
        </circle>
        {/* Second fingertip dot riding the right path (mirrored motion). */}
        <circle r="3.5" fill={TERRA} opacity="0.9">
          <animateMotion dur="3.4s" repeatCount="indefinite" keyPoints="0;1;1;0" keyTimes="0;0.55;0.85;1" calcMode="linear">
            <mpath href="#jaw-drain-r" />
          </animateMotion>
        </circle>
      </FaceBase>
    </svg>
  );
}

/* ---------- Under-eye tap ---------- */
export function UnderEyeTapSVG({ size = 72, label = "Under-eye tap demo" }) {
  // Three dots pulse sequentially under each eye
  const dots = [
    { cx: 40, cy: 68 }, { cx: 46, cy: 68 }, { cx: 52, cy: 68 },
    { cx: 68, cy: 68 }, { cx: 74, cy: 68 }, { cx: 80, cy: 68 },
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" role="img" aria-label={label} data-testid="svg-eye-tap">
      <FaceBase>
        {dots.map((d, i) => {
          const delay = (i % 3) * 0.35; // stagger left-to-right within each side
          return (
            <circle key={i} cx={d.cx} cy={d.cy} r="1.6" fill={TERRA}>
              <animate attributeName="r" values="1.6;3.6;1.6" dur="1.1s" begin={`${delay}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.35;1;0.35" dur="1.1s" begin={`${delay}s`} repeatCount="indefinite" />
            </circle>
          );
        })}
      </FaceBase>
    </svg>
  );
}

/* Convenience switch by template_id */
export function FacialExerciseSVG({ templateId, size = 72 }) {
  if (templateId === "core-face-stretch-drain") return <FaceStretchDrainSVG size={size} />;
  if (templateId === "core-face-cheek-lift") return <CheekLiftSVG size={size} />;
  if (templateId === "core-face-jaw-glide") return <JawGlideSVG size={size} />;
  if (templateId === "core-face-eye-tap") return <UnderEyeTapSVG size={size} />;
  if (templateId === "core-face-full-yoga") return <FullFaceYogaSVG size={size} />;
  return null;
}
